import { Accordion, AccordionSummary, Grid, Link, Paper, TextField, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Theme, createStyles, withStyles } from "@material-ui/core/styles";
import AddBox from "@material-ui/icons/AddBox";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FiberNewIcon from "@material-ui/icons/FiberNew";
import InfoIcon from "@material-ui/icons/Info";
import RefreshIcon from "@material-ui/icons/Refresh";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Form, Formik } from "formik";
import parse from "html-react-parser";
import MaterialTable from "material-table";
import { DropzoneArea } from "material-ui-dropzone";
import EnumDesignation from "utils/enum/EnumDesignation";
import LabelsContext from "../../../lang/LabelsContext";
import { getTypeFromNameFile, handleFileType } from "../../../utils/Utils";
import { tableIcons } from "../../system/material-table-utils/MaterialTableUtils";
import CorrectionService, { CorrectionServiceType } from "./CorrectionService";
import useStyles from "./CorrectionStyle";

const SIZE_1MB = 1048576;

const DESIGNATION_HIDE_ACTION = [
  EnumDesignation.OVERVIEW,
  EnumDesignation.FULL_MANUSCRIPT,
  EnumDesignation.FULL_ARTICLE,
  EnumDesignation.FIGURE_PDF,
];

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      fontWeight: "bolder",
      fontSize: 14,
    },
  })
)(TableCell);

export default function Correction() {
  const classes = useStyles();
  const { Labels } = LabelsContext();

  const {
    uploadFile,
    filesList,
    setFilesList,
    designationOptions,
    openDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleResetDialog,
    files,
    uploading,
    submit,
    correctionText,
    selectedDesignation,
    filesByAssistant,
    articleRevision,
  }: CorrectionServiceType = CorrectionService();

  return (
    <div>
      <CssBaseline />
      <div className={classes.layout}>
        <Typography component="span" variant="h5">
          Manuscript correction
        </Typography>
        <br />
        <br />
        <Typography component="span">
          The assistant editor encountered an error while checking your manuscript. <br />
          Please read the message carefully and correct your manuscript accordingly <br />
          Please only resubmit the document which was requested for correction. It is not necessary to reupload all the
          documents again.
        </Typography>
        <Grid container xs={12} sm={8}>
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <StyledTableCell colSpan={2}>Error description</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow key={1}>
                  <TableCell scope="row">Issue</TableCell>
                  <TableCell>Manuscript does not follow template</TableCell>
                </TableRow>
                <TableRow key={2}>
                  <TableCell scope="row">Description</TableCell>
                  <TableCell>{parse(correctionText ?? "")}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Paper className={classes.paper}>
          {Object.keys(filesByAssistant)
            .reverse()
            .map((id: any, index: number) => (
              <Accordion defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                  <Typography className={classes.heading}>Revision #{id}</Typography>
                </AccordionSummary>
                <MaterialTable
                  icons={tableIcons}
                  columns={[
                    { title: Labels.FILE_NAME, field: "id", hidden: true },
                    {
                      title: Labels.FILE_NAME,
                      field: "name",
                      render: (rowData: any) => (
                        <Link href={rowData?.location} color="inherit" component="a" target="_blank">
                          {rowData?.name}
                        </Link>
                      ),
                    },
                    { title: Labels.FILE_DESIGNATION, field: "designation" },
                    { title: Labels.FILE_TYPE, field: "type" },
                    { title: "new", field: "new", hidden: true, defaultSort: "desc" },
                  ]}
                  data={filesByAssistant[id]}
                  options={{
                    actionsColumnIndex: -1,
                    pageSize: 10,
                    search: false,
                  }}
                  editable={{
                    isDeleteHidden: ({ designation }: any) =>
                      DESIGNATION_HIDE_ACTION.includes(designation) || Number(articleRevision) !== Number(id),
                    onRowDelete: (newData) =>
                      new Promise((resolve, reject) => {
                        resolve(setFilesList(filesList.filter((file: any) => file.id !== newData.id)));
                      }),
                  }}
                  actions={
                    articleRevision !== id
                      ? [
                          ({ new: newFile }) => ({
                            icon: () => <FiberNewIcon style={{ color: "green" }} />,
                            hidden: !newFile,
                            position: "row",
                            onClick: () => {},
                          }),
                          ({ designation, id: fileId }) => ({
                            icon: () => <RefreshIcon />,
                            tooltip: `replace`,
                            hidden:
                              DESIGNATION_HIDE_ACTION.includes(designation) || Number(articleRevision) !== Number(id),
                            position: "row",
                            onClick: () => handleOpenDialog(fileId),
                          }),
                          ({ designation, id: fileId }) => ({
                            icon: () => <InfoIcon>{Labels.ADD}</InfoIcon>,
                            tooltip: "document generated by the system / assistant",
                            hidden: !DESIGNATION_HIDE_ACTION.includes(designation),
                            onClick: () => {},
                          }),
                          {
                            icon: () => <AddBox>{Labels.ADD}</AddBox>,
                            tooltip: Labels.ADD,
                            hidden: Number(articleRevision) !== Number(id),
                            isFreeAction: true,
                            onClick: (event) => handleOpenDialog(),
                          },
                        ]
                      : []
                  }
                  title=""
                />
              </Accordion>
            ))}
        </Paper>
        <Formik
          enableReinitialize
          initialValues={{
            designation: selectedDesignation
              ? designationOptions.find((d: any) => d.code === selectedDesignation?.designation)
              : designationOptions[0],
            size: "",
            type: "",
            comments: "",
          }}
          onSubmit={() => {}}
        >
          {({ handleBlur, values, touched, errors, handleChange, handleSubmit, setFieldValue }) => (
            <Form>
              <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">{Labels.FILE_UPLOAD}</DialogTitle>
                <DialogContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={designationOptions}
                        getOptionLabel={(option: any) => option.name}
                        value={values.designation}
                        disabled={!!selectedDesignation}
                        onChange={(event, value) => setFieldValue("designation", value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            margin="normal"
                            id="designation"
                            name="designation"
                            onBlur={handleBlur}
                            label={Labels.FILE_DESIGNATION}
                            error={touched.designation && Boolean(errors.designation)}
                            helperText={touched.designation && errors.designation}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <DropzoneArea
                      dropzoneProps={{ disabled: !values.designation }}
                      acceptedFiles={handleFileType(values.designation?.code)}
                      maxFileSize={SIZE_1MB * 50} //50MB
                      onChange={(files: File[]) => {
                        if (files.length > 0) {
                          handleResetDialog(
                            true,
                            setFieldValue,
                            files,
                            (files[0].size / SIZE_1MB).toFixed(2) + " mb",
                            getTypeFromNameFile(files[0].name)
                          );
                        }
                      }}
                      onDelete={(file: File) => {
                        handleResetDialog(false, setFieldValue, null, "", "");
                      }}
                      filesLimit={1}
                    />
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled={true}
                        variant="outlined"
                        margin="normal"
                        id="size"
                        name="size"
                        onBlur={handleBlur}
                        label={Labels.SIZE}
                        value={values.size}
                        error={touched.size && Boolean(errors.size)}
                        helperText={touched.size && errors.size}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled={true}
                        variant="outlined"
                        margin="normal"
                        id="type"
                        name="type"
                        label={`${Labels.FILE_TYPE}:`}
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.type && Boolean(errors.type)}
                        helperText={touched.type && errors.type}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="comments"
                        name="comments"
                        onBlur={handleBlur}
                        label={Labels.COMMENTS}
                        error={touched.comments && Boolean(errors.comments)}
                        helperText={touched.comments && errors.comments}
                        fullWidth
                        multiline
                        rows={5}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      handleCloseDialog();
                      handleResetDialog(false, setFieldValue, [], "", "");
                    }}
                    color="primary"
                  >
                    {Labels.CANCEL}
                  </Button>
                  <Button
                    disabled={uploading}
                    onClick={() => {
                      uploadFile(files, values);
                      handleResetDialog(false, setFieldValue, null, "", "");
                      handleCloseDialog();
                    }}
                    color="primary"
                    variant={"contained"}
                  >
                    {Labels.UPLOAD}
                  </Button>
                </DialogActions>
              </Dialog>
            </Form>
          )}
        </Formik>

        <Typography component="span">
          When you are done adjusting your manuscript and figures, press the button below to send the article back to
          the assistant editor.
        </Typography>
        <br />
        <div className={classes.buttons}>
          <Button onClick={submit} color="primary" variant="contained" className={classes.button}>
            {Labels.SUBMIT}
          </Button>
        </div>
      </div>
    </div>
  );
}
